import { useState, useEffect } from 'react'

interface ResponseDelayEditorProps {
  initialDelay: number
  connectionId: number
  onUpdate: (newDelay: number) => void
}

export default function ResponseDelayEditor({ 
  initialDelay, 
  connectionId,
  onUpdate 
}: ResponseDelayEditorProps) {
  const [delay, setDelay] = useState(initialDelay)
  const [showVariation, setShowVariation] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    setDelay(initialDelay)
  }, [initialDelay])

  const handleDelayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDelay = parseInt(e.target.value)
    setDelay(newDelay)
    setSaved(false)
  }

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    
    try {
      // Appeler onUpdate pour que le parent sauvegarde
      onUpdate(delay)
      setSaved(true)
      
      // Reset le message "Sauvegardé" après 3 secondes
      setTimeout(() => setSaved(false), 3000)
    } catch (error) {
      console.error('Failed to save delay:', error)
    } finally {
      setSaving(false)
    }
  }

  // Calculer le délai final avec variation
  const minDelay = showVariation ? Math.max(5, Math.floor(delay * 0.8)) : delay
  const maxDelay = showVariation ? Math.ceil(delay * 1.2) : delay

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Délai de réponse
        </h3>
        {saved && (
          <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Sauvegardé
          </span>
        )}
      </div>

      <div className="space-y-3">
        {/* Slider */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs text-gray-500 dark:text-gray-400">
              Délai de base
            </label>
            <span className="text-sm font-mono text-gray-900 dark:text-white">
              {delay} secondes
            </span>
          </div>
          
          <input
            type="range"
            min="10"
            max="60"
            step="5"
            value={delay}
            onChange={handleDelayChange}
            className="w-full h-2 bg-gray-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-green-600"
          />
          
          <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500">
            <span>10s</span>
            <span>30s</span>
            <span>60s</span>
          </div>
        </div>

        {/* Variation checkbox */}
        <label className="flex items-start gap-3 cursor-pointer group">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={showVariation}
              onChange={(e) => setShowVariation(e.target.checked)}
              className="w-4 h-4 text-green-600 bg-gray-100 dark:bg-zinc-700 border-gray-300 dark:border-zinc-600 rounded focus:ring-green-500 focus:ring-2"
            />
          </div>
          <div className="flex-1">
            <div className="text-sm text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400 transition">
              Variation aléatoire (±20%)
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Rend les réponses plus naturelles et évite les bans WhatsApp
            </div>
          </div>
        </label>

        {/* Aperçu délai final */}
        <div className="rounded-lg bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 p-3">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Délai final de réponse
          </div>
          <div className="text-lg font-semibold text-gray-900 dark:text-white">
            {showVariation ? (
              <span>
                {minDelay}s - {maxDelay}s
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                  (aléatoire)
                </span>
              </span>
            ) : (
              <span>
                {delay}s
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                  (fixe)
                </span>
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            ⚡ Temps avant que l'agent ne réponde sur WhatsApp
          </div>
        </div>

        {/* Recommandations */}
        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
          <div className="flex items-start gap-2">
            <span className="text-green-600 dark:text-green-400">✓</span>
            <span><strong>10-20s</strong> : Réponse rapide (usage normal)</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-600 dark:text-yellow-400">⚠</span>
            <span><strong>30-40s</strong> : Réponse prudente (gros volume)</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-red-600 dark:text-red-400">⚡</span>
            <span><strong>50-60s</strong> : Réponse lente (sécurité max)</span>
          </div>
        </div>

        {/* Bouton Sauvegarder */}
        <button
          onClick={handleSave}
          disabled={saving || delay === initialDelay}
          className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {saving ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sauvegarde...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Sauvegarder le délai
            </>
          )}
        </button>
      </div>
    </div>
  )
}
